import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FiPlus, FiEdit2, FiTrash2, FiUsers, FiBriefcase, FiEye, FiFileText } from 'react-icons/fi';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import TableFilters, { SortableTh } from '../components/TableFilters';
import { formatCpfCnpj, formatCep } from '../utils/formatCpfCnpj';
import { buscarPorCep } from '../services/viaCep';
import './Empreendimentos.css';

const Empreendimentos = () => {
  const { hasPermission } = useAuth();
  const [empreendimentos, setEmpreendimentos] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterMunicipio, setFilterMunicipio] = useState('');
  const [filterGrupo, setFilterGrupo] = useState('');
  const [municipiosOptions, setMunicipiosOptions] = useState([]);
  const [gruposOptions, setGruposOptions] = useState([]);
  const [sort, setSort] = useState('nome');
  const [order, setOrder] = useState('asc');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [formData, setFormData] = useState({
    nome: '',
    cnpj_cpf: '',
    municipio: '',
    status: 'ativo',
    descricao: '',
    cep: '',
    logradouro: '',
    numero: '',
    complemento: '',
    bairro: '',
    uf: ''
  });
  const [cepLoading, setCepLoading] = useState(false);

  const carregarEmpreendimentos = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (filterStatus) params.set('status', filterStatus);
      if (filterMunicipio) params.set('municipio', filterMunicipio);
      if (filterGrupo) params.set('grupo_empresarial_id', filterGrupo);
      params.set('sort', sort);
      params.set('order', order);
      params.set('page', page);
      params.set('limit', limit);
      const response = await api.get(`/api/empreendimentos?${params}`);
      const data = response.data;
      if (data && typeof data === 'object' && 'data' in data) {
        setEmpreendimentos(data.data);
        setTotal(data.total || 0);
      } else {
        setEmpreendimentos(Array.isArray(data) ? data : []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      console.error('Erro ao carregar empreendimentos:', error);
      alert('Erro ao carregar empreendimentos');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus, filterMunicipio, filterGrupo, sort, order, page, limit]);

  const carregarFiltrosOptions = useCallback(async () => {
    try {
      const res = await api.get('/api/empreendimentos/filtros');
      setMunicipiosOptions(res.data?.municipios || []);
      setGruposOptions(res.data?.grupos_empresariais || []);
    } catch {
      setMunicipiosOptions([]);
      setGruposOptions([]);
    }
  }, []);

  useEffect(() => {
    carregarFiltrosOptions();
  }, [carregarFiltrosOptions]);

  useEffect(() => {
    carregarEmpreendimentos();
  }, [carregarEmpreendimentos]);

  const handleSort = (col) => {
    setSort(col);
    setOrder((o) => (sort === col && o === 'asc' ? 'desc' : 'asc'));
    setPage(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/api/empreendimentos/${editing}`, formData);
      } else {
        await api.post('/api/empreendimentos', formData);
      }
      setShowModal(false);
      setEditing(null);
      setFormData({ nome: '', cnpj_cpf: '', municipio: '', status: 'ativo', descricao: '', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', uf: '' });
      carregarEmpreendimentos();
    } catch (error) {
      console.error('Erro ao salvar empreendimento:', error);
      alert('Erro ao salvar empreendimento');
    }
  };

  const handleEdit = (emp) => {
    setEditing(emp.id);
    setFormData({
      nome: emp.nome || '',
      cnpj_cpf: formatCpfCnpj(emp.cnpj_cpf || ''),
      municipio: emp.municipio || '',
      status: emp.status || 'ativo',
      descricao: emp.descricao || '',
      cep: formatCep(emp.cep || ''),
      logradouro: emp.logradouro || '',
      numero: emp.numero || '',
      complemento: emp.complemento || '',
      bairro: emp.bairro || '',
      uf: emp.uf || ''
    });
    setShowModal(true);
  };

  const handleCepBlur = async () => {
    const digits = String(formData.cep || '').replace(/\D/g, '');
    if (digits.length !== 8) return;
    setCepLoading(true);
    try {
      const endereco = await buscarPorCep(formData.cep);
      if (!endereco.erro) {
        setFormData((prev) => ({
          ...prev,
          logradouro: endereco.logradouro || prev.logradouro,
          bairro: endereco.bairro || prev.bairro,
          municipio: endereco.localidade || prev.municipio,
          uf: endereco.uf || prev.uf
        }));
      }
    } catch (e) {
      console.warn('Busca CEP:', e);
    } finally {
      setCepLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este empreendimento?')) {
      return;
    }
    try {
      await api.delete(`/api/empreendimentos/${id}`);
      carregarEmpreendimentos();
    } catch (error) {
      console.error('Erro ao excluir empreendimento:', error);
      alert('Erro ao excluir empreendimento');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      ativo: 'badge-success',
      inativo: 'badge-secondary',
      suspenso: 'badge-warning'
    };
    return badges[status] || 'badge-secondary';
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="empreendimentos-page">
      <div className="page-header">
        <h1 className="page-title">
          <FiBriefcase className="page-title-icon" />
          Empreendimentos
        </h1>
        {hasPermission('enterprise', 'create') && (
          <button className="btn btn-primary" onClick={() => {
            setEditing(null);
            setFormData({ nome: '', cnpj_cpf: '', municipio: '', status: 'ativo', descricao: '', cep: '', logradouro: '', numero: '', complemento: '', bairro: '', uf: '' });
            setShowModal(true);
          }}>
            <FiPlus /> Novo Empreendimento
          </button>
        )}
      </div>

      <TableFilters
        search={search}
        onSearchChange={setSearch}
        filterRows={[['search', 'status', 'municipio', 'grupo_empresarial']]}
        filters={[
          {
            key: 'status',
            label: 'Status',
            value: filterStatus,
            onChange: (v) => { setFilterStatus(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              { value: 'ativo', label: 'Ativo' },
              { value: 'inativo', label: 'Inativo' },
              { value: 'suspenso', label: 'Suspenso' }
            ]
          },
          {
            key: 'municipio',
            label: 'Município',
            value: filterMunicipio,
            onChange: (v) => { setFilterMunicipio(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(municipiosOptions.map((m) => ({ value: m, label: m })))
            ]
          },
          {
            key: 'grupo_empresarial',
            label: 'Grupo empresarial',
            value: filterGrupo,
            onChange: (v) => { setFilterGrupo(v); setPage(1); },
            options: [
              { value: '', label: 'Todos' },
              ...(gruposOptions.map((g) => ({ value: g.id, label: g.nome })))
            ]
          }
        ]}
        total={total}
        page={page}
        limit={limit}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        searchPlaceholder="Buscar por nome, CNPJ ou município..."
      />

      {empreendimentos.length === 0 ? (
        <div className="empty-state">
          <p>Nenhum empreendimento encontrado.</p>
          <button className="btn btn-primary" onClick={() => setShowModal(true)}>
            <FiPlus /> Criar primeiro empreendimento
          </button>
        </div>
      ) : (
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <SortableTh sortKey="nome" currentSort={sort} currentOrder={order} onSort={handleSort}>Nome</SortableTh>
                <SortableTh sortKey="cnpj_cpf" currentSort={sort} currentOrder={order} onSort={handleSort}>CNPJ/CPF</SortableTh>
                <SortableTh sortKey="municipio" currentSort={sort} currentOrder={order} onSort={handleSort}>Município</SortableTh>
                <SortableTh sortKey="grupo_empresarial_nome" currentSort={sort} currentOrder={order} onSort={handleSort}>Grupo empresarial</SortableTh>
                <th>Licenças</th>
                <th>Usuários</th>
                <SortableTh sortKey="status" currentSort={sort} currentOrder={order} onSort={handleSort}>Status</SortableTh>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {empreendimentos.map((emp) => (
                <tr key={emp.id}>
                  <td>
                    <Link to={`/empreendimentos/${emp.id}`} className="link-detalhes">{emp.nome}</Link>
                  </td>
                  <td>{emp.cnpj_cpf || '-'}</td>
                  <td>{emp.municipio || '-'}</td>
                  <td>{emp.grupo_empresarial_nome || '-'}</td>
                  <td>
                    <span className="emp-count" title="Licenças">
                      <FiFileText className="emp-count-icon" /> {emp.licencas_count ?? 0}
                    </span>
                  </td>
                  <td>
                    <span className="emp-count" title="Usuários">
                      <FiUsers className="emp-count-icon" /> {emp.usuarios_count ?? 0}
                    </span>
                  </td>
                  <td>
                    <span className={`badge ${getStatusBadge(emp.status)}`}>
                      {emp.status || 'ativo'}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      <Link
                        to={`/empreendimentos/${emp.id}`}
                        className="btn btn-sm btn-outline btn-icon"
                        title="Visualizar detalhes"
                        style={{ textDecoration: 'none' }}
                      >
                        <FiEye />
                      </Link>
                      {hasPermission('enterprise', 'edit') && (
                        <button
                          className="btn btn-sm btn-secondary btn-icon"
                          onClick={() => handleEdit(emp)}
                          title="Editar"
                        >
                          <FiEdit2 />
                        </button>
                      )}
                      {hasPermission('users', 'view') && (
                        <Link
                          to={`/empreendimentos/${emp.id}/usuarios`}
                          className="btn btn-sm btn-info btn-icon"
                          title="Usuários"
                        >
                          <FiUsers />
                        </Link>
                      )}
                      {hasPermission('enterprise', 'archive') && (
                        <button
                          className="btn btn-sm btn-danger btn-icon"
                          onClick={() => handleDelete(emp.id)}
                          title="Excluir"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => {
          setShowModal(false);
          setEditing(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? <><FiEdit2 /> Editar</> : <><FiPlus /> Novo</>} Empreendimento</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Nome *</label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>CNPJ/CPF</label>
                <input
                  type="text"
                  value={formData.cnpj_cpf}
                  onChange={(e) => setFormData({ ...formData, cnpj_cpf: formatCpfCnpj(e.target.value) })}
                  placeholder="00.000.000/0000-00 ou 000.000.000-00"
                />
              </div>

              <div className="form-group">
                <label>CEP</label>
                <input
                  type="text"
                  value={formData.cep}
                  onChange={(e) => setFormData({ ...formData, cep: formatCep(e.target.value) })}
                  onBlur={handleCepBlur}
                  placeholder="00000-000"
                  maxLength={9}
                />
                {cepLoading && <span className="form-help">Buscando endereço...</span>}
                <span className="form-help">Preencha o CEP para buscar Estado, Cidade e Endereço.</span>
              </div>

              {(String(formData.cep || '').replace(/\D/g, '').length === 8 || formData.logradouro || formData.uf) && (
                <div className="form-address-block">
                  <div className="form-group">
                    <label>Estado (UF)</label>
                    <input
                      type="text"
                      value={formData.uf}
                      onChange={(e) => setFormData({ ...formData, uf: e.target.value.toUpperCase().slice(0, 2) })}
                      placeholder="UF"
                      maxLength={2}
                    />
                  </div>
                  <div className="form-group">
                    <label>Cidade</label>
                    <input
                      type="text"
                      value={formData.municipio}
                      onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                      placeholder="Município"
                    />
                  </div>
                  <div className="form-group">
                    <label>Endereço</label>
                    <input
                      type="text"
                      value={formData.logradouro}
                      onChange={(e) => setFormData({ ...formData, logradouro: e.target.value })}
                      placeholder="Rua, avenida, rodovia..."
                    />
                  </div>
                  <div className="form-group form-group-inline">
                    <div className="form-group">
                      <label>Número</label>
                      <input
                        type="text"
                        value={formData.numero}
                        onChange={(e) => setFormData({ ...formData, numero: e.target.value })}
                        placeholder="Nº ou km"
                      />
                    </div>
                    <div className="form-group">
                      <label>Complemento</label>
                      <input
                        type="text"
                        value={formData.complemento}
                        onChange={(e) => setFormData({ ...formData, complemento: e.target.value })}
                        placeholder="Sala, bloco..."
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                >
                  <option value="ativo">Ativo</option>
                  <option value="inativo">Inativo</option>
                  <option value="suspenso">Suspenso</option>
                </select>
              </div>
              <div className="form-group">
                <label>Descrição</label>
                <textarea
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  rows="3"
                />
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowModal(false);
                  setEditing(null);
                }}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary">
                  {editing ? 'Atualizar' : 'Criar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Empreendimentos;
